import type { AxiosError } from "axios";

/** Axios error whose payload may carry field validation errors or a message. */
export type ValidationError = AxiosError<{
  errors?: Record<string, string[]>;
  message?: string;
}>;

/** Title/description pair used to feed a toast notification. */
export interface ErrorToastContent {
  title: string;
  description: string;
}

const hasUniqueError = (field?: string[]): boolean =>
  !!field?.includes("validation.unique");

/**
 * Resolves the toast content for a node 422 validation error.
 * Shared by node create/update mutations to avoid duplicated branches.
 */
export const resolveNodeValidationError = (
  errors: Record<string, string[]> | undefined,
): ErrorToastContent => {
  if (hasUniqueError(errors?.display_name)) {
    return {
      title: "Nombre duplicado",
      description:
        "Ya existe un nodo con este nombre. Por favor, elige un nombre diferente.",
    };
  }
  if (hasUniqueError(errors?.ref_name)) {
    return {
      title: "Referencia duplicada",
      description:
        "Ya existe un nodo con esta referencia. Por favor, cambia la referencia.",
    };
  }
  return {
    title: "Error de validación",
    description:
      "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.",
  };
};

/**
 * Resolves the toast content for a failed node update (PATCH /node/{id}).
 * Shared by the update and reset mutations. A 409 means the backend gave up
 * after retrying a concurrent change, so its `message` is shown as-is to let
 * the user know they can save again.
 */
export const resolveNodeUpdateError = (
  error: ValidationError,
): ErrorToastContent => {
  switch (error?.response?.status) {
    case 422:
      return resolveNodeValidationError(error.response.data?.errors);
    case 409:
      return {
        title: "Error al actualizar nodo",
        description:
          error.response.data?.message ||
          "Otra persona o proceso está modificando este flujo. Intente guardar de nuevo en unos segundos.",
      };
    case 404:
      return {
        title: "Nodo no encontrado",
        description: "El nodo que intenta actualizar no existe.",
      };
    case 403:
      return {
        title: "Sin permisos",
        description: "No tiene permisos para actualizar este nodo.",
      };
    default:
      return {
        title: "Error al actualizar nodo",
        description: "Ocurrió un error al intentar actualizar el nodo.",
      };
  }
};

/**
 * Resolves the toast content for a form 422 validation error.
 * Shared by form create/update mutations to avoid duplicated branches.
 */
export const resolveFormValidationError = (
  errors: Record<string, string[]> | undefined,
): ErrorToastContent => {
  if (hasUniqueError(errors?.name)) {
    return {
      title: "Nombre duplicado",
      description:
        "Ya existe un formulario con este nombre. Por favor, elige un nombre diferente.",
    };
  }
  return {
    title: "Error de validación",
    description:
      "Los datos del formulario no son válidos. Verifica la información e intenta nuevamente.",
  };
};
